import type {
  CheckoutUser,
  UserLookupStatus,
} from '@trmediaab/zebra-types/trhub';
import { useCallback, useState } from 'react';
import useSWR from 'swr';

import type { ErrorWithMessage } from '../types/ErrorWithMessage';
import type { OnAuthentication } from '../types/OnAuthenticationHandler';
import { ensureErrorWithMessage } from '../utils/ensureErrorWithMessage';
import request from '../utils/request';
import { useImmutableState } from '.';

const noop = () => {};

export default function useUser(
  inputValues?: { email: string } | Record<string, unknown>, // TODO define type
  onAuthentication: OnAuthentication = noop,
) {
  const [authError, setAuthError] = useState<ErrorWithMessage | null>(null);
  const [lookupError, setLookupError] = useState<ErrorWithMessage | null>(null);
  const [verifyError, setVerifyError] = useState<ErrorWithMessage | null>(null);
  const [status, setStatus] = useState<UserLookupStatus | null>(null);
  const { site } = useImmutableState();

  // Try to get current user by auth token
  const {
    data: user,
    error: validateError,
    isValidating,
    mutate,
  } = useSWR<CheckoutUser, ErrorWithMessage>(`user?site=${site}`, {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
  });

  const changeEmail = useCallback(() => {
    setStatus(null);
  }, []);

  // Request the lookup endpoint
  const lookupByEmailRequest = useCallback(
    async (resetPassword = false) => {
      setLookupError(null);

      try {
        if (inputValues == null) {
          throw new Error('E-postadress saknas');
        }

        const data = await request<{ status: UserLookupStatus }>(
          'user/lookup',
          {
            method: 'POST',
            body: {
              email: inputValues.email,
              resetPassword,
            },
          },
        );

        setStatus(data.status);
      } catch (error) {
        setLookupError(ensureErrorWithMessage(error));
      }
    },
    [inputValues],
  );

  // Check if a user exists
  const lookupByEmail = useCallback(() => {
    lookupByEmailRequest();
  }, [lookupByEmailRequest]);

  // Request a verification code
  const forgotPassword = useCallback(() => {
    lookupByEmailRequest(true);
  }, [lookupByEmailRequest]);

  // Authenticate a user by email and password
  const authenticate = useCallback(async () => {
    setAuthError(null);

    try {
      const { user } = await request<{ user: CheckoutUser }>('user/auth', {
        method: 'POST',
        body: {
          ...inputValues,
          site,
        },
      });

      mutate(user, false);
      onAuthentication(user);
    } catch (error) {
      setAuthError(ensureErrorWithMessage(error));
    }
  }, [inputValues, mutate, onAuthentication, site]);

  // Verify a email by a four digit code
  const verify = useCallback(async () => {
    setVerifyError(null);
    try {
      const { user } = await request<{ user: CheckoutUser }>('user/verify', {
        method: 'POST',
        body: {
          ...inputValues,
          site,
        },
      });

      // update current user in cache
      mutate(user, false);
      onAuthentication(user);
    } catch (error) {
      setVerifyError(ensureErrorWithMessage(error));
    }
  }, [inputValues, mutate, onAuthentication, site]);

  // Switching account
  const logout = useCallback(async () => {
    await request('user/logout', {
      method: 'POST',
    });

    mutate(undefined, false); // TODO - test this change
    //Reset the state
    setStatus(null);
    onAuthentication();
  }, [mutate, onAuthentication]);

  return {
    user,
    status,
    validateError,
    authError,
    lookupError,
    verifyError,
    isValidating,
    lookupByEmail,
    forgotPassword,
    authenticate,
    verify,
    logout,
    changeEmail,
  };
}
